import { t } from '@lingui/core/macro';
import type { NoddiDropdownOption, NoddiDropdownOptionGroup } from 'noddi-ui-web';
import { NoddiDropdown } from 'noddi-ui-web';
import { getDepartmentOrganizationDisplay } from 'noddi-web';
import { useCustomerDepartmentSelection } from '../../hooks/useCustomerDepartmentSelection';
import { useUserGroupSelection } from '../../hooks/useUserGroupSelection';
import { useSelectDepartment } from '../../stores/customerDepartmentStore';
import { isUserGroupOrganizationContext, type SelectableUserGroup } from '../../utils/userGroupContext';

type Props = {
  className?: string;
  wrapperClassName?: string;
};

type CustomerContextOption = NoddiDropdownOption<string> & {
  userGroupId: number;
  departmentId?: number;
};

export const UserGroupDropdown = ({ className, wrapperClassName }: Props) => {
  const { selectUserGroup, hasMultipleSelectableUserGroups, selectedUserGroup, selectableUserGroups, userData } =
    useUserGroupSelection();
  const { selectedDepartment } = useCustomerDepartmentSelection();
  const selectDepartment = useSelectDepartment();

  const hasMultipleDepartments = selectableUserGroups.some((userGroup) => userGroup.departments.length > 1);
  if (!userData || (!hasMultipleSelectableUserGroups && !hasMultipleDepartments)) {
    return null;
  }

  const toOptions = (userGroup: SelectableUserGroup): CustomerContextOption[] =>
    userGroup.departments.length > 0
      ? userGroup.departments.map((department) => {
          const { primaryName, secondaryName } = getDepartmentOrganizationDisplay(
            department.name,
            department.organizationName
          );
          return {
            label: primaryName,
            description: secondaryName,
            value: `${userGroup.id}:${department.id}`,
            userGroupId: userGroup.id,
            departmentId: department.id
          };
        })
      : [{ label: userGroup.name, value: `${userGroup.id}`, userGroupId: userGroup.id }];

  const personalOptions = selectableUserGroups.filter((ug) => !isUserGroupOrganizationContext(ug)).flatMap(toOptions);
  const organizationOptions = selectableUserGroups.filter(isUserGroupOrganizationContext).flatMap(toOptions);
  const allOptions = [...personalOptions, ...organizationOptions];
  const selectedValue = selectedUserGroup
    ? selectedDepartment
      ? `${selectedUserGroup.id}:${selectedDepartment.id}`
      : `${selectedUserGroup.id}`
    : undefined;

  // The profile type is a section header instead of a `· Organization` suffix, so the
  // name gets the full width — org names are long and were the part that truncated.
  const groups: NoddiDropdownOptionGroup<string>[] = [
    ...(personalOptions.length > 0 ? [{ label: t`Personal`, icon: 'User' as const, options: personalOptions }] : []),
    ...(organizationOptions.length > 0
      ? [{ label: t`Organizations`, icon: 'Building2' as const, options: organizationOptions }]
      : [])
  ];

  return (
    <NoddiDropdown<string>
      startIcon={isUserGroupOrganizationContext(selectedUserGroup) ? 'Building2' : 'CircleUserRound'}
      groups={groups}
      placeholder={t`Select profile`}
      selectedOption={allOptions.find((option) => option.value === selectedValue) ?? null}
      onSelect={(option) => {
        const context = allOptions.find((candidate) => candidate.value === option.value);
        if (!context) {
          return;
        }
        selectUserGroup(context.userGroupId);
        if (context.departmentId != null) {
          selectDepartment(`${userData.user.id}:${context.userGroupId}`, context.departmentId);
        }
      }}
      wrapperClassName={wrapperClassName}
      className={className}
      // Section headers eat vertical room, and an org account can have a long list —
      // the default 160px on mobile shows barely two rows under a header.
      menuClassName='max-h-96'
    />
  );
};
